import { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router';

import { useAtomValue } from 'jotai';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import { BannerMessage, Typography } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { getNameFromLanguage } from '@/utils/language';
import { tokenHasChangesAtom } from '@/utils/permissions-widget';
import { cn } from '@/utils/tailwind';

import 'react-smartbanner/dist/main.css';

import { computeBannersToShow } from './helpers';

import { userPrefsAtom } from '@/atoms/userPrefs';

const WEB_ALERT_BANNER_KEY = 'web-alert-banner-dismissed';

type DismissedBannerIds = string[];

const isDismissedBannerIds = (value: unknown): value is DismissedBannerIds =>
  Array.isArray(value) && value.every(id => typeof id === 'string');

const WebAlertBannerContainer = () => {
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    const stored = localStorage.getItem(WEB_ALERT_BANNER_KEY);
    if (!stored) return [];
    try {
      const parsed: unknown = JSON.parse(stored);
      return isDismissedBannerIds(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [now] = useState(() => Math.floor(Date.now() / 1000));
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: auth } = useAuth();
  const locationsWithAtLeastLeaderPermission =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const { webAlertBanner } = useFlags();
  const ldClient = useLDClient();

  const isLeaderOrOperator = locationsWithAtLeastLeaderPermission.length > 0;
  const hasComplianceEnabled =
    auth?.features.settings?.compliance?.enabled === true;
  const complianceDetail = ldClient?.variationDetail(
    'xp-compliance-plans',
    false,
  );
  const hasXpCompliancePlans = complianceDetail?.value === true;
  const isInCompliancePhaseSegment =
    complianceDetail?.reason?.kind === 'RULE_MATCH';
  const isInChainWideRollout = complianceDetail?.reason?.kind === 'FALLTHROUGH';

  const bannersToShow = computeBannersToShow(
    webAlertBanner,
    dismissedIds,
    {
      isLeaderOrOperator,
      hasComplianceEnabled,
      hasXpCompliancePlans,
      isInCompliancePhaseSegment,
      isInChainWideRollout,
    },
    now,
  );

  useEffect(() => {
    localStorage.setItem(WEB_ALERT_BANNER_KEY, JSON.stringify(dismissedIds));
  }, [dismissedIds]);

  // This is to detect if the Modified Token Applied banner is already showing.
  const tokenHasChanges = useAtomValue(tokenHasChangesAtom);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height =
          containerRef.current.offsetHeight + (tokenHasChanges ? 20 : 0); // 24px is the height of the token banner
        document.documentElement.style.setProperty(
          '--web-alert-banner-height',
          `${height}px`,
        );
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [bannersToShow, tokenHasChanges]);

  const closeBanner = (id: string) => {
    setDismissedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  const { language } = useAtomValue(userPrefsAtom);

  return (
    <div
      ref={containerRef}
      className={cn('fixed z-5000 w-full', {
        'mt-5': tokenHasChanges,
      })}
    >
      {bannersToShow.length && !window.native.isNative
        ? bannersToShow.map(b => (
            <BannerMessage
              key={b.id}
              onClose={() => {
                if (b.id) {
                  closeBanner(b.id);
                }
              }}
              severity="info"
              show
            >
              {getNameFromLanguage(b.message, language)}{' '}
              {b.link && (
                <Link data-testid="alert-banner" target="_self" to={b.link.url}>
                  <Typography
                    variant="body2"
                    className="inline text-white! underline"
                  >
                    {getNameFromLanguage(b.link.text, language)}
                  </Typography>
                </Link>
              )}
            </BannerMessage>
          ))
        : null}
    </div>
  );
};

export default WebAlertBannerContainer;
